import type { FieldDefinition, ObjectDefinition } from "./types";

/* Client-side CSV parsing + per-cell validation — a faithful mirror of
 * api/src/data/csv.ts and api/src/model/validate.ts so the importer's live
 * preview matches exactly what the server would accept on import. */

export function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  const src = text.replace(/\r\n?/g, "\n");
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') { if (src[i + 1] === '"') { field += '"'; i++; } else inQuotes = false; }
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); field = ""; row = []; }
    else field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  const nonEmpty = rows.filter((r) => r.some((cell) => cell.trim() !== ""));
  if (!nonEmpty.length) return { headers: [], rows: [] };
  const [headers, ...body] = nonEmpty;
  return { headers: headers.map((h) => h.trim()), rows: body };
}

/** Coerce/validate one raw cell against a field def. Empty → null (allowed). */
export function coerceCell(f: FieldDefinition, raw: string): { ok: boolean; value: unknown; error?: string } {
  if (raw === "" || raw == null) return { ok: true, value: null };
  switch (f.type) {
    case "number": case "currency": case "percent": {
      const n = Number(raw);
      return Number.isFinite(n) ? { ok: true, value: n } : { ok: false, value: null, error: "Must be a number" };
    }
    case "boolean":
      return raw === "true" || raw === "false" ? { ok: true, value: raw === "true" } : { ok: false, value: null, error: "true or false" };
    case "select":
      return f.options && !f.options.includes(raw) ? { ok: false, value: null, error: `One of: ${f.options.join(", ")}` } : { ok: true, value: raw };
    case "email":
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(raw) ? { ok: true, value: raw } : { ok: false, value: null, error: "Invalid email" };
    case "date":
      return !Number.isNaN(Date.parse(raw)) ? { ok: true, value: raw } : { ok: false, value: null, error: "Invalid date" };
    default:
      return { ok: true, value: raw };
  }
}

export interface MappedRow { attributes: Record<string, unknown>; errors: { field: string; message: string }[]; }

/** Apply a column→field mapping to parsed rows, validating each cell.
 * `mapping[header] = fieldKey | ""` ("" = skip that column). */
export function mapRows(obj: ObjectDefinition, headers: string[], rows: string[][], mapping: Record<string, string>): MappedRow[] {
  const byKey = new Map(obj.fields.map((f) => [f.key, f]));
  return rows.map((cells) => {
    const attributes: Record<string, unknown> = {};
    const errors: { field: string; message: string }[] = [];
    headers.forEach((h, i) => {
      const key = mapping[h];
      if (!key) return;
      const f = byKey.get(key);
      if (!f) return;
      const { ok, value, error } = coerceCell(f, (cells[i] ?? "").trim());
      if (!ok) errors.push({ field: key, message: error ?? "Invalid" });
      else if (value !== null) attributes[key] = value;
    });
    return { attributes, errors };
  });
}
